import { useEffect, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { isTokenExpired, getSession } from '../services/api/sessionApi';
import { STORAGE_KEYS } from '../constants';
import { usePermissions } from '../context/PermissionContext';

/** How often (ms) the background guard checks for token expiry. */
const AUTH_POLL_INTERVAL_MS = 60_000; // every 60 seconds

/**
 * ProtectedRoute — guards pages behind authentication.
 *
 * Auth strategy (post httpOnly-cookie migration):
 * 1. Quick localStorage check (session metadata) for instant UX.
 * 2. Backend /api/session call for authoritative verification.
 *    The httpOnly JWT cookie is sent automatically by the browser.
 * 3. Periodic background guard: while children are rendered, checks every
 *    60 s whether the token has expired. If expired, attempts silent
 *    refresh; on failure, revokes auth state → redirect to /login.
 * 4. Visibility-change guard: on tab re-focus, immediately checks and
 *    refreshes if needed (covers sleep/wake, tab switching).
 */
function ProtectedRoute({ children, requiredAgent }) {
  const [authenticated, setAuthenticated] = useState(null); // null = loading
  const [accessDenied, setAccessDenied] = useState(false);
  const { initPermissions } = usePermissions();

  /**
   * Attempt silent refresh. Returns true if session is still alive.
   */
  const attemptRefresh = useCallback(async () => {
    try {
      // Use single-flight refresh to prevent race conditions with
      // visibility handlers and other tabs refreshing simultaneously.
      const { requestTokenRefresh } = await import('../services/api/apiClient');
      return await requestTokenRefresh();
    } catch {
      return false;
    }
  }, []);

  // ── Initial auth check (runs once on mount) ──────────────────────
  useEffect(() => {
    let cancelled = false;
    
    const checkAuth = async () => {
      try {
        // Fast check: is there stored session metadata?
        const agentType = localStorage.getItem(STORAGE_KEYS.AGENT_TYPE);
        const tokenExpiry = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);

        if (!agentType || !tokenExpiry) {
          if (!cancelled) setAuthenticated(false);
          return;
        }

        // Refresh if the stored expiry says expired OR if this tab has
        // no in-memory token (e.g. new tab where the module-level token
        // hasn't been populated yet from localStorage on first load).
        const { getAuthToken } = await import('../services/api/apiClient');
        if (isTokenExpired() || !getAuthToken()) {
          const refreshed = await attemptRefresh();
          if (!refreshed) {
            if (!cancelled) setAuthenticated(false);
            return;
          }
        }

        // If requiredAgent is specified, check access before hitting backend
        if (requiredAgent && agentType !== requiredAgent) {
          if (!cancelled) setAccessDenied(true);
          return;
        }

        // Authoritative check: call backend with httpOnly cookie
        try {
          const sessionData = await getSession();
          if (!cancelled) {
            // Re-validate agent from authoritative source
            if (requiredAgent && sessionData.agent !== requiredAgent) {
              setAccessDenied(true);
            } else {
              // Initialize permissions here so every protected route (not
              // just /org) has `hasPermission()` populated by the time its
              // children render. Previously this happened inside
              // OrgChatInner, which meant deep-linking / refreshing a
              // sibling route (e.g. /org/procedures) saw an empty
              // permission set.
              initPermissions(sessionData);
              setAuthenticated(true);
            }
          }
        } catch (backendError) {
          const isDeviceDenied =
            backendError.message === 'DEVICE_NOT_APPROVED' || backendError.message === 'DEVICE_ID_MISSING';
          if (isDeviceDenied) {
            // RC-2 (STAFF-DEVICE-LOCK): Cognito already minted tokens for
            // this browser session — revoke them so a denied device never
            // keeps a working session against any other endpoint.
            const { signOut } = await import('../services/auth/authApi');
            await signOut();
          }
          // If backend returns 401/403, user is not authenticated
          if (isDeviceDenied || backendError.message === 'UNAUTHORIZED' || backendError.message === 'FORBIDDEN' || backendError.message === 'ACCOUNT_DISABLED') {
            if (!cancelled) setAuthenticated(false);
          } else {
            // Network error — only trust local session if token is NOT expired.
            // If token is expired + backend is unreachable, don't resurrect a dead session.
            if (isTokenExpired()) {
              console.warn('[ProtectedRoute] Backend unreachable + token expired — not authenticated');
              if (!cancelled) setAuthenticated(false);
            } else {
              console.warn('[ProtectedRoute] Backend unreachable, trusting local session (token still valid)');
              if (!cancelled) setAuthenticated(true);
            }
          }
        }
        
      } catch (err) {
        console.error('[ProtectedRoute] Auth check failed:', err.message);
        if (!cancelled) setAuthenticated(false);
      }
    };
    
    checkAuth();
    
    return () => { cancelled = true; };
  }, [requiredAgent, attemptRefresh, initPermissions]);

  // ── Background guard: periodic expiry check + visibility change ──
  useEffect(() => {
    // Only run while the user is authenticated (children are rendered)
    if (authenticated !== true) return;

    let intervalId = null;

    /**
     * Check token expiry and try refresh. If refresh fails, kick to login.
     */
    const guardCheck = async () => {
      if (!isTokenExpired()) return; // still valid — nothing to do

      console.debug('[ProtectedRoute] Background guard: token expired, attempting refresh');
      const ok = await attemptRefresh();
      if (!ok) {
        // Refresh failed — session dead, force redirect
        console.warn('[ProtectedRoute] Background guard: refresh failed, logging out');
        setAuthenticated(false);
      }
    };

    // Periodic interval check
    intervalId = setInterval(guardCheck, AUTH_POLL_INTERVAL_MS);

    // Visibility-change: immediate check when tab regains focus
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        guardCheck();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [authenticated, attemptRefresh]);

  if (authenticated === null) {
    return (
      <div className="overlay-backdrop" role="status" aria-live="polite">
        <div className="overlay-card">
          <div className="spinner" />
          <div>
            <div className="overlay-title">Checking access…</div>
            <div className="overlay-subtle">Preparing your chat</div>
          </div>
        </div>
      </div>
    );
  }
  
  if (accessDenied) {
    return <Navigate to="/access-denied" replace />;
  }
  
  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

export default ProtectedRoute;
